import * as React from "react"
import { type DOMAttributes } from "react"
import { Link, type LinkProps } from "react-router-dom"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import * as ToggleGroup from "@radix-ui/react-toggle-group"
import styled from "styled-components"

const TOGGLE_TRANSITION_DURATION = 200

/*
  STYLES
*/

const ToggleContainer = styled(ToggleGroup.Root)<{ disabled?: boolean }>`
  --toggle-buttons-v-padding: 2px;
  --toggle-buttons-h-padding: 2.2px;
  --toggle-buttons-background: ${colors.secondary10};
  --toggle-buttons-selection-color: ${colors.secondary};
  --toggle-buttons-selection-background: ${colors.white};
  --toggle-buttons-selection-shadow: 0 0 4px 0 ${colors.translucentSecondary40};
  --toggle-buttons-option-color: ${colors.secondary70};
  --toggle-buttons-hover-background: ${colors.secondary20};
  --toggle-buttons-border-radius: 99px;
  --toggle-buttons-inner-radius: calc(
    var(--toggle-buttons-border-radius) - var(--toggle-buttons-v-padding)
  );

  &.circles {
    --toggle-buttons-v-padding: 4px;
    --toggle-buttons-h-padding: var(--toggle-buttons-v-padding);
  }

  position: relative;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: var(--toggle-buttons-v-padding) var(--toggle-buttons-h-padding);
  background: var(--toggle-buttons-background);
  border-radius: var(--toggle-buttons-border-radius);

  &[disabled] {
    pointer-events: none;
  }
`

const SelectionBackground = styled.div`
  position: absolute;
  height: calc(100% - 2 * var(--toggle-buttons-v-padding));
  border-radius: var(--toggle-buttons-inner-radius);
  background: var(--toggle-buttons-selection-background);
  box-shadow: var(--toggle-buttons-selection-shadow);

  transition:
    transform ${TOGGLE_TRANSITION_DURATION}ms ease-in-out,
    width ${TOGGLE_TRANSITION_DURATION}ms ease-in-out;

  &.no-transition {
    transition-duration: 1ms;
  }
`

const ToggleButton = styled(ToggleGroup.Item)`
  --toggle-button-v-padding: 4px;
  --toggle-button-h-padding: 12px;
  ${ToggleContainer}.circles & {
    --toggle-button-v-padding: 4px;
    --toggle-button-h-padding: var(--toggle-button-v-padding);
  }

  svg {
    max-width: 20px;
    max-height: 20px;

    &.circles {
      margin-left: unset;
    }
  }

  padding: var(--toggle-button-v-padding) var(--toggle-button-h-padding);

  position: relative;
  display: flex;
  gap: 4px;
  border-radius: var(--toggle-buttons-inner-radius);
  width: auto;
  height: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  transition:
    color 300ms ease-in-out,
    background-color 300ms ease;
  font-weight: ${fonts.weight.heavy};
  line-height: 19px;

  &[data-state="on"] {
    cursor: default;
    color: var(--toggle-buttons-selection-color);
    path {
      stroke: var(--toggle-buttons-selection-color);
    }
  }

  &[data-state="off"] {
    color: var(--toggle-buttons-option-color);
    &:hover:not([data-disabled]) {
      background-color: var(--toggle-buttons-hover-background);
    }
    path {
      stroke: var(--toggle-buttons-option-color);
    }
  }

  &[data-disabled] {
    cursor: default;
  }
`

/*
  INTERFACES
*/

export interface ToggleButtonsProps<T extends string>
  extends Omit<DOMAttributes<HTMLDivElement>, "onChange"> {
  defaultValue?: T
  onChange?: (value: T) => void
  className?: string
  circles?: boolean
  disabled?: boolean
}

interface OptionProps {
  className?: string
  solidIcon?: boolean
}

type ToggleOptionProps<T extends string> = OptionProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value"> & {
    value: T
  }

type LinkOptionProps = OptionProps & Omit<LinkProps, "to"> & { to: string; value?: string }

/*
  COMPONENT
*/

/**
 * ToggleButtons is a component that creates a group of mutually exclusive toggle buttons.
 * It provides a styled container with a sliding selection indicator that moves to highlight
 * the currently selected option.
 *
 * ```
 * <ToggleButtons defaultValue="option1" onChange={(value) => console.log(value)}>
 *   <ToggleOption value="option1">First Option</ToggleOption>
 *   <ToggleOption value="option2">Second Option</ToggleOption>
 * </ToggleButtons>
 * ```
 */
export function ToggleButtons<T extends string>({
  children,
  defaultValue,
  onChange,
  className,
  circles,
  disabled,
  ...domProps
}: React.PropsWithChildren<ToggleButtonsProps<T>>) {
  const [selected, setSelected] = React.useState<T | undefined>(defaultValue)
  const [skipTransitions, setSkip] = React.useState<string | undefined>("no-transition")
  const [isTransitioning, startTransition] = React.useTransition()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const selectionRef = React.useRef<HTMLDivElement>(null)

  // Update the selection background position and size
  const updateSelectionBackground = React.useCallback(
    (newSelected: T | undefined) => {
      const container = containerRef.current
      const selection = selectionRef.current
      if (!container || !selection) {
        return
      }
      // Add selected class to newly selected element and update selection background
      const selectedElement = containerRef?.current?.querySelector(
        `[data-value="${newSelected}"]`
      ) as HTMLElement

      if (!selectedElement) {
        return
      }

      const containerPadding = parseInt(getComputedStyle(container).paddingLeft)
      selection.style.width = `${selectedElement.offsetWidth}px`
      selection.style.transform = `translateX(${selectedElement.offsetLeft - containerPadding}px)`

      return selection
    },
    [containerRef]
  )

  const updateSelection = React.useCallback(
    (newSelected: T | undefined) => {
      setSelected(newSelected)
      updateSelectionBackground(newSelected)
    },
    [updateSelectionBackground]
  )

  React.useEffect(() => {
    const updateTimeout = setTimeout(() => {
      updateSelection(defaultValue)
    }, 10)

    const skipTimeout = setTimeout(() => {
      setSkip(undefined)
    }, 10)

    return () => {
      clearTimeout(updateTimeout)
      clearTimeout(skipTimeout)
    }
  }, [defaultValue, updateSelection])

  const onValueChange = React.useCallback(
    (value: T) => {
      startTransition(() => {
        updateSelection(value)
        onChange?.(value)
        return new Promise((resolve) => setTimeout(resolve, TOGGLE_TRANSITION_DURATION))
      })
    },
    [onChange, updateSelection]
  )

  const classNames = [className, circles ? "circles" : ""].join(" ")
  return (
    <ToggleContainer
      {...domProps}
      className={classNames}
      ref={containerRef}
      onValueChange={onValueChange}
      type="single"
      value={selected}
      disabled={disabled || isTransitioning}
    >
      <SelectionBackground ref={selectionRef} className={skipTransitions} />
      {children}
    </ToggleContainer>
  )
}

/**
 * ToggleOption represents an individual button within a ToggleButtons group.
 * Must be used as a child of ToggleButtons component.
 *
 * ```tsx
 * <ToggleOption value="option1">Option Text</ToggleOption>
 * ```
 */
export function ToggleOption<T extends string>({
  children,
  value,
  className,
  solidIcon,
  ...buttonProps
}: React.PropsWithChildren<ToggleOptionProps<T>>) {
  return (
    <ToggleButton {...buttonProps} data-value={value} className={className} value={value}>
      {children}
    </ToggleButton>
  )
}

/**
 * ToggleLinkOption represents an individual anchor within a ToggleButtons group.
 * Must be used as a child of ToggleButtons component.
 *
 * ```tsx
 * <ToggleLinkOption to="/path1">Option Text</ToggleOption>
 * ```
 */
export function ToggleLinkOption({
  children,
  to,
  value,
  className,
  solidIcon,
  ...linkProps
}: React.PropsWithChildren<LinkOptionProps>) {
  return (
    <Link to={to} {...linkProps}>
      <ToggleOption className={className} value={value ?? to} solidIcon={solidIcon}>
        {children}
      </ToggleOption>
    </Link>
  )
}
